export const DateUtil = {

    // --------------------------------------------------------------------------------------------
    // Date
    // --------------------------------------------------------------------------------------------

    /**
     * Convert Date to Swiss German Date string (DD.MM.YYYY)
     */
    convertDateToString(date) {
        const isoDateString = date.toISOString().split('T')[0];
        const [year, month, day] = isoDateString.split('-');
        return `${day}.${month}.${year}`;
    },

    /**
     * Convert Date to ISO Date string (YYYY-MM-DD). Avoid day shift -1 for built-in UTC timezone!
     */
    convertDateToIsoString(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Ensure two digits
        const day = String(date.getDate()).padStart(2, '0'); // Ensure two digits
        return `${year}-${month}-${day}`;
    },

    /**
     * Convert Swiss German Date string (DD.MM.YYYY) to Date object
     */
    convertStringToDate(swissDateString) {
        const [day, month, year] = swissDateString.split('.').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed in JS Date
    },

    /**
     * Convert Swiss German Date string (DD.MM.YYYY) to 2-digit Day.
     */
    convertStringToDay(swissDateString) {
        return swissDateString.slice(0, 2)
    },

    /**
     * Convert ISO Date string (YYYY-MM-DD) to Date
     */
    convertIsoStringToDate(isoDateString) {
        if (isoDateString === null) {
            return null;
        }
        return new Date(isoDateString.split("T")[0]);
    },

    /**
     * Convert ISO Date string (YYYY-MM-DD) to Date String (DD.MM.YYYY)
     */
    convertIsoStringToString(isoDateString) {
        if (isoDateString === null) {
            return null;
        }
        if ( !isoDateString.includes("-") ) {
            return isoDateString
        }
        const [year, month, day] = isoDateString.split("-");
        return [day, month, year].join(".");
    },

    /**
     * Get the current date as a Date object.
     * Keeps the time part as is, i.e. don't set it to 00:00:00!
     */
    getCurrentDate() {
        return new Date();
    },

    /**
     * Get current date as a string format (DD.MM.YYYY) for swiss german (de-CH).
     */
    getCurrentDateAsString() {
        return DateUtil.convertDateToString(DateUtil.getCurrentDate())
    },

    /**
     * Get the current date as an ISO Date string (YYYY-MM-DD)
     */
    getCurrentDateAsIsoString() {
        return DateUtil.convertDateToIsoString(DateUtil.getCurrentDate());
    },

    /**
     * Get the next day as a Date object.
     * Keeps the time part as is.
     */
    getNextDayDate() {
        const nextDay = new Date();
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay;
    },

    /**
     * Get next day as a string format (DD.MM.YYYY) for swiss german (de-CH).
     */
    getNextDayDateAsString() {
        return DateUtil.convertDateToString(DateUtil.getNextDayDate());
    },

    /**
     * Get next day as an ISO Date string (YYYY-MM-DD).
     */
    getNextDayDateAsIsoString() {
        return DateUtil.convertDateToIsoString(DateUtil.getNextDayDate());
    },

    // --------------------------------------------------------------------------------------------
    // DateTime
    // --------------------------------------------------------------------------------------------

    /**
     * Convert Date to Swiss German DateTime string (DD.MM.YYYY HH:mm:ss)
     */
    convertDateTimeToString(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    },

    /**
     * Convert DateTime to ISO DateTime string (YYYY-MM-DDTHH:mm:ss)
     */
    convertDateTimeToIsoString(date) {
        if (!date) return null;
        // If date is a number (timestamp) from new Date(), convert it to a Date object
        const dateObj = typeof date === "number" ? new Date(date) : date;
        return dateObj.toISOString();
    },

    /**
     * Convert Swiss German DateTime string (DD.MM.YYYY HH:mm:ss) to Date object
     */
    convertStringToDateTime(swissGermanDateTimeString) {
        const [datePart, timePart] = swissGermanDateTimeString.split(' ');
        const [day, month, year] = datePart.split('.').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);

        return new Date(year, month - 1, day, hours, minutes, seconds); // Month is 0-indexed
    },

    /**
     * Convert ISO DateTime string (YYYY-MM-DDTHH:mm:ss) to Date
     */
    convertIsoStringToDateTime(isoDateTimeString) {
        return new Date(isoDateTimeString);
    },

    /**
     * Get the current DateTime as a Date object
     */
    getCurrentDateTime() {
        return new Date();
    },

    /**
     * Get the current DateTime as a Swiss German DateTime string (DD.MM.YYYY HH:mm:ss)
     */
    getCurrentDateTimeAsString() {
        const now = new Date();
        const isoString = now.toISOString(); // Get the ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)

        // Extract the date and time parts from the ISO string
        const [datePart, timePart] = isoString.split('T');
        const [year, month, day] = datePart.split('-'); // YYYY-MM-DD
        const [hours, minutes, secondsWithMillis] = timePart.split(':'); // HH:mm:ss.sss
        const seconds = secondsWithMillis.split('.')[0]; // Get only seconds without milliseconds

        // Format the output as DD.MM.YYYY HH:mm:ss
        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    },

    /**
     * Get the current DateTime as an ISO DateTime string (YYYY-MM-DDTHH:mm:ss)
     */
    getCurrentDateTimeAsIsoString() {
        const now = new Date();
        return now.toISOString().split('.')[0];
    }

};
